import * as React from "react";
import { NavigationIcon } from "../Icon/NavigationIcon";
import { Logo } from "../Logo";

export type SideNavItem = {
  id: string;
  label: string;
  href?: string; // Optional - if not provided and has children, becomes a category
  icon?: React.ReactNode;
  isActive?: boolean;
  isExpanded?: boolean; // For categories with children
  children?: SideNavItem[]; // If present, this becomes a category
};

export type SideNavSection = {
  id: string;
  title?: string; // Optional section title
  items: SideNavItem[];
};

export type SideNavProps = {
  topItems?: SideNavItem[] | SideNavSection[]; // Union type for backward compatibility
  bottomItems?: SideNavItem[];
  bottomContent?: React.ReactNode; // For app-specific content like WorkspaceSwitcher
  logo?: React.ReactNode;
  showLogo?: boolean;
  linkComponent?: React.ElementType;
  className?: string;
  mobileMenuId?: string;
  sidebarCollapseId?: string;
};

/**
 * SideNav
 *
 * Vertical navigation component for application sidebars.
 * Supports nested navigation items, sections, collapsible categories, and mobile-responsive behavior.
 */
export const SideNav: React.FC<SideNavProps> = ({
  topItems = [],
  bottomItems = [],
  bottomContent,
  logo,
  showLogo = true,
  linkComponent: LinkComponent = "a",
  className = "",
  mobileMenuId = "app-mobile-menu",
  sidebarCollapseId = "app-sidebar-collapse",
}) => {
  // Helper function to determine if topItems is using the new section format
  const isSectionFormat = (items: SideNavItem[] | SideNavSection[]): items is SideNavSection[] => {
    return items.length > 0 && "items" in items[0];
  };

  // Helper function to normalize topItems to sections for consistent rendering
  const normalizeToSections = (items: SideNavItem[] | SideNavSection[]): SideNavSection[] => {
    if (isSectionFormat(items)) {
      return items;
    }
    // Convert plain items array to a single section
    return [{ id: "default-section", items: items }];
  };

  const renderSection = (section: SideNavSection, sectionIndex: number, totalSections: number): React.ReactNode => {
    return (
      <div key={section.id} className="space-y-0.5">
        {/* Optional section title */}
        {section.title && (
          <div className="px-6 py-1 text-xs font-medium text-text-secondary uppercase tracking-wider">
            {section.title}
          </div>
        )}

        {/* Section items */}
        <div className="space-y-0.5">{section.items.map((item) => renderNavItem(item))}</div>

        {/* Section separator - border between sections (but not after the last section) */}
        {sectionIndex < totalSections - 1 && <div className="border-b border-border-subtle mx-6 my-2" />}
      </div>
    );
  };

  const renderNavItem = (item: SideNavItem, isNested = false): React.ReactNode => {
    // If item has children, render as category
    if (item.children && item.children.length > 0) {
      const categoryId = `category-${item.id}`;
      return (
        <div key={item.id} className="group/category">
          {/* Hidden checkbox for expand/collapse state */}
          <input
            type="checkbox"
            id={categoryId}
            className="peer/category hidden"
            defaultChecked={item.isExpanded}
            aria-hidden="true"
          />

          {/* Category header - acts as label for checkbox */}
          <label
            htmlFor={categoryId}
            className="flex items-center gap-3 px-6 py-1 text-text-primary hover:bg-background-hover hover:text-text-primary cursor-pointer transition-all duration-200"
          >
            {item.icon && (
              <span className="flex-shrink-0 w-3.5 h-3.5 flex items-center justify-center transition-all duration-200">
                {item.icon}
              </span>
            )}
            <span className="transition-all duration-200">{item.label}</span>
            {/* Spacer to push chevron to the right */}
            <span className="flex-1" />
            {/* Chevron icon for expand/collapse */}
            <svg
              className="w-4 h-4 transform transition-transform duration-200 peer-checked/category:rotate-180 ml-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </label>

          {/* Category children - hidden by default, shown when checkbox is checked */}
          <div className="hidden peer-checked/category:block bg-background-subtle border-l-2 border-border-subtle ml-6">
            {item.children.map((child) => renderNavItem(child, true))}
          </div>
        </div>
      );
    }

    // Otherwise render as regular link
    const linkProps = {
      href: item.href || "#",
      className: `flex items-center gap-3 transition-all duration-200 hover:bg-background-hover ${
        isNested ? "pl-8 pr-6 py-1 text-sm" : "px-6 py-1"
      } ${
        item.isActive
          ? `bg-action-brand/10 text-action-brand ${!isNested ? "border-l-2 border-action-brand" : ""}`
          : "text-text-primary hover:text-text-primary"
      }`,
      "aria-current": item.isActive ? ("page" as const) : undefined,
    };

    const linkContent = (
      <>
        {item.icon && !isNested && (
          <span className="flex-shrink-0 w-3.5 h-3.5 flex items-center justify-center transition-all duration-200">
            {item.icon}
          </span>
        )}
        <span className="transition-all duration-200">{item.label}</span>
      </>
    );

    if (LinkComponent === "a") {
      return (
        <a key={item.id} {...linkProps}>
          {linkContent}
        </a>
      );
    }

    return (
      <LinkComponent key={item.id} {...linkProps}>
        {linkContent}
      </LinkComponent>
    );
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header with logo and collapse button */}
      <div className="flex items-center justify-between h-[60px] px-6 border-b border-border-default">
        {showLogo && (
          <div className="relative top-[-1px] transition-all duration-200">
            {logo ? (
              // If custom logo is provided, use it
              logo
            ) : (
              // Otherwise use the default Logo component
              <Logo showWordmark={true} width={120} />
            )}
          </div>
        )}

        {/* Desktop collapse toggle */}
        <label
          htmlFor={sidebarCollapseId}
          className="hidden md:flex items-center justify-center w-8 h-8 rounded hover:bg-background-hover cursor-pointer transition-all duration-200 relative top-[-1px]"
          aria-label="Collapse sidebar"
        >
          <NavigationIcon name="Sidebar" />
        </label>

        {/* Mobile close button */}
        <label
          htmlFor={mobileMenuId}
          className="md:hidden flex items-center justify-center w-8 h-8 rounded hover:bg-background-hover cursor-pointer transition-colors"
          aria-label="Close menu"
        >
          {/* Close icon */}
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </label>
      </div>

      {/* Navigation sections */}
      <nav className="flex-1 flex flex-col overflow-hidden">
        {/* Top section - scrollable */}
        {topItems && topItems.length > 0 && (
          <div className="flex-1 overflow-y-auto py-2 scrollbar-hide">
            {normalizeToSections(topItems).map((section, index, sections) =>
              renderSection(section, index, sections.length)
            )}
          </div>
        )}

        {/* Bottom section - fixed at bottom with enhanced separator */}
        {bottomItems && bottomItems.length > 0 && (
          <div className="flex-shrink-0 border-t bg-background-muted/30">
            {/* Bottom navigation items with background and proper spacing */}
            <div className="mt-2 pb-2 space-y-0.5">{bottomItems.map((item) => renderNavItem(item))}</div>
          </div>
        )}

        {/* Bottom content - for app-specific components like WorkspaceSwitcher */}
        {bottomContent && (
          <div className="flex-shrink-0 border-t border-border-default">
            <div>{bottomContent}</div>
          </div>
        )}
      </nav>
    </div>
  );
};
